export class ApiError extends Error {
  status: number
  data: unknown

  code?: string

  constructor(message: string, status: number, data: unknown, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
    this.code = code
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  params?: Record<string, string | number | boolean | undefined | null>
  body?: BodyInit | Record<string, string | number | boolean | undefined | null>
  headers?: Record<string, string>
  formUrlEncoded?: boolean
  timeout?: number
}

function toQuery(params?: RequestOptions['params']): string {
  if (!params) return ''
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    search.set(key, String(value))
  })
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

function resolveBody(options: RequestOptions): BodyInit | undefined {
  if (!options.body) return undefined
  if (options.formUrlEncoded && typeof options.body === 'object' && !(options.body instanceof URLSearchParams) && !(options.body instanceof FormData)) {
    const params = new URLSearchParams()
    Object.entries(options.body).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      params.set(key, String(value))
    })
    return params.toString()
  }
  if (typeof options.body === 'object' && !(options.body instanceof URLSearchParams) && !(options.body instanceof FormData) && !(options.body instanceof Blob)) {
    return JSON.stringify(options.body)
  }
  return options.body as BodyInit
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method || 'GET'
  const headers: Record<string, string> = {
    ...(options.headers || {}),
  }
  const params: RequestOptions['params'] = { ...(options.params || {}) }

  if (import.meta.env.DEV) {
    headers.Authorization = `Basic ${btoa('coral:coral')}`
    params.language = 'ky'
  }

  if (typeof window !== 'undefined' && window.orderSession) {
    headers.orderSession = window.orderSession
  }

  const body = resolveBody(options)
  if (options.formUrlEncoded) {
    headers['Content-Type'] = 'application/x-www-form-urlencoded'
  } else if (body && !headers['Content-Type'] && typeof body === 'string' && body.startsWith('{')) {
    headers['Content-Type'] = 'application/json'
  }

  const controller = options.timeout ? new AbortController() : null
  const timer = options.timeout
    ? window.setTimeout(() => controller?.abort(), options.timeout)
    : null

  let response: Response
  try {
    response = await fetch(`${path}${toQuery(params)}`, {
      method,
      headers,
      body: method === 'GET' ? undefined : body,
      credentials: 'include',
      signal: controller?.signal,
    })
  } catch (error) {
    if (timer) window.clearTimeout(timer)
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('Request timeout', 0, null, 'ECONNABORTED')
    }
    throw error
  }

  if (timer) window.clearTimeout(timer)

  const contentType = response.headers.get('content-type') || ''
  const data = contentType.includes('application/json')
    ? await response.json()
    : await response.text()

  if (!response.ok) {
    throw new ApiError(`Request failed: ${response.status}`, response.status, data)
  }

  return data as T
}
